/**
 * The Heart Cut — Minimum Order Quantity for Pocket Pours
 * -----------------------------------------------------------
 * Enforces a combined minimum quantity across every "Pocket Pour"
 * cart line item, regardless of which whisky product it belongs to.
 * Matched by SKU suffix (configured in snippets/thc-moq-group.liquid),
 * with a variant-title fallback for Pocket Pour variants that have
 * no SKU set at all.
 *
 * This script is loaded from layout/theme.liquid, i.e. on EVERY page —
 * required because the cart drawer/notification markup (and its
 * checkout + dynamic payment buttons) lives in the DOM site-wide,
 * not just on /cart.
 *
 * Enforcement level: storefront (cart form + checkout/express button
 * hiding). This blocks the normal checkout flow for regular customers
 * on any Shopify plan. It is not unbypassable at the API level — true
 * checkout-level enforcement requires Shopify Plus Functions.
 *
 * Performance note: earlier version polled /cart.js on a flat 2s
 * setInterval on every page, forever — real, avoidable overhead. This
 * version reacts instantly to cart:updated/cart:refresh events (which
 * fire on the normal add/remove/change flows in this theme) and uses a
 * much lighter fallback poll that backs off the longer the cart state
 * stays unchanged, and stops entirely on pages with nothing to protect.
 */
(function () {
  var config = window.THC_MOQ;
  if (!config || !config.skuSuffix || !config.minimumQuantity) return;

  var suffix = config.skuSuffix.toUpperCase();
  var minQty = config.minimumQuantity;
  var bannerClass = 'thc-moq-banner';
  var noticeClass = 'thc-moq-product-notice';
  var noticeText = 'Pocket Pours: minimum order of ' + minQty + ' in total, any mix.';

  // Selectors covering both the standard checkout button and Shopify's
  // dynamic/express checkout buttons (Shop Pay, Apple Pay, Google Pay,
  // PayPal, etc.), which do NOT go through the "checkout" button at all.
  var CHECKOUT_SELECTOR = 'button[name="checkout"], a[href^="/checkout"], input[name="checkout"]';
  var EXPRESS_CHECKOUT_SELECTOR =
    '.shopify-payment-button, [data-shopify="payment-button"], .additional-checkout-buttons, .cart__buttons--additional-checkout-buttons';

  // Fallback-poll tuning: starts fast, backs off while nothing changes,
  // resets to fast whenever the cart actually changes.
  var POLL_INTERVAL_MIN = 4000;
  var POLL_INTERVAL_MAX = 20000;
  var pollInterval = POLL_INTERVAL_MIN;
  var idleTicks = 0;
  var lastStateKey = null;
  var pollTimer = null;

  function matchesGroup(item) {
    var sku = (item.sku || '').toUpperCase();
    if (sku && sku.slice(-suffix.length) === suffix) return true;
    // Fallback for Pocket Pour variants with no SKU set — match on
    // variant title so they aren't silently missed by the rule.
    if (!sku && item.variant_title && /pocket pour/i.test(item.variant_title)) {
      return true;
    }
    if (!sku && item.title && /pocket pour/i.test(item.title)) return true;
    return false;
  }

  function getBannerContainers() {
    var forms = document.querySelectorAll('form[action="/cart"], form[action$="/cart"]');
    var containers = [];
    forms.forEach(function (form) {
      var el = form.querySelector('.' + bannerClass);
      if (!el) {
        el = document.createElement('div');
        el.className = bannerClass;
        form.insertBefore(el, form.firstChild);
      }
      containers.push(el);
    });
    return containers;
  }

  function showBanner(shortfall) {
    var containers = getBannerContainers();
    if (!containers.length) return;
    var itemWord = shortfall === 1 ? 'Pocket Pour' : 'Pocket Pours';
    var message =
      'Add ' + shortfall + ' more ' + itemWord + ' (any mix) to reach the minimum order of ' +
      minQty + ' before you can check out.';
    containers.forEach(function (el) {
      el.textContent = message;
      el.style.display = 'block';
    });
  }

  function hideBanner() {
    document.querySelectorAll('.' + bannerClass).forEach(function (el) {
      el.style.display = 'none';
    });
  }

  function getGroupQuantity(cart) {
    var total = 0;
    cart.items.forEach(function (item) {
      if (matchesGroup(item)) total += item.quantity;
    });
    return total;
  }

  function setCheckoutDisabled(disabled) {
    document.querySelectorAll(CHECKOUT_SELECTOR).forEach(function (btn) {
      if (disabled) {
        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('thc-moq-disabled');
      } else {
        btn.removeAttribute('aria-disabled');
        btn.classList.remove('thc-moq-disabled');
      }
    });

    // Dynamic/express checkout buttons bypass the button above entirely
    // (e.g. Shop Pay in the cart drawer) — hide them outright rather
    // than trying to intercept their click, since some render inside
    // an iframe that a page-level click listener can never see.
    document.querySelectorAll(EXPRESS_CHECKOUT_SELECTOR).forEach(function (el) {
      el.style.display = disabled ? 'none' : '';
    });
  }

  function hideExpressCheckoutOnPocketPourProduct() {
    if (!config.currentProductIsPocketPour) return;
    // On a Pocket Pour's own product page, express checkout can send
    // just that product straight to checkout, skipping the cart (and
    // the combined-minimum rule) altogether — so it stays hidden here
    // regardless of what's already in the cart. "Add to cart" still works.
    document.querySelectorAll(EXPRESS_CHECKOUT_SELECTOR).forEach(function (el) {
      el.style.display = 'none';
    });
  }

  function insertProductPageNotice() {
    if (!config.showNotice) return;
    // Find the product's "Add to cart" form using standard Shopify
    // markup (form posting to /cart/add) rather than a theme-specific
    // class, so this works regardless of the exact product template.
    var form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    if (form.querySelector('.' + noticeClass)) return; // already inserted

    var note = document.createElement('p');
    note.className = noticeClass;
    note.textContent = noticeText;

    var submit = form.querySelector('button[type="submit"], input[type="submit"]');
    if (submit && submit.parentNode) {
      submit.parentNode.insertBefore(note, submit.nextSibling);
    } else {
      form.appendChild(note);
    }
  }

  // Is there anything on this page for the fallback poll to protect?
  // If not (e.g. a blog post with no cart form, no checkout buttons,
  // and not a Pocket Pour product page), don't bother polling at all.
  function hasAnythingToProtect() {
    if (config.currentProductIsPocketPour) return true;
    if (document.querySelector('form[action="/cart"], form[action$="/cart"]')) return true;
    if (document.querySelector(CHECKOUT_SELECTOR)) return true;
    if (document.querySelector(EXPRESS_CHECKOUT_SELECTOR)) return true;
    return false;
  }

  function scheduleNextPoll() {
    if (pollTimer) {
      clearTimeout(pollTimer);
      pollTimer = null;
    }
    if (!hasAnythingToProtect()) return;
    pollTimer = setTimeout(evaluate, pollInterval);
  }

  function evaluate() {
    hideExpressCheckoutOnPocketPourProduct();
    insertProductPageNotice();
    fetch('/cart.js')
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var groupQty = getGroupQuantity(cart);
        var disabled = groupQty > 0 && groupQty < minQty;
        var stateKey = groupQty + ':' + disabled;

        if (stateKey === lastStateKey) {
          idleTicks += 1;
          if (idleTicks >= 3) {
            pollInterval = Math.min(pollInterval * 2, POLL_INTERVAL_MAX);
          }
        } else {
          idleTicks = 0;
          pollInterval = POLL_INTERVAL_MIN;
        }
        lastStateKey = stateKey;

        if (disabled) {
          showBanner(minQty - groupQty);
          setCheckoutDisabled(true);
        } else {
          hideBanner();
          setCheckoutDisabled(false);
        }
        // Re-apply the product-page rules last: they must win even when
        // the cart-level check above would otherwise re-show buttons.
        hideExpressCheckoutOnPocketPourProduct();
        insertProductPageNotice();
      })
      .catch(function () {
        /* fail open on the cart-level rule, but keep the product-page
           rules in place either way */
      })
      .then(scheduleNextPoll);
  }

  function onCartEvent() {
    // A real cart change just happened — react immediately and reset
    // the backoff so the next few checks are fast again.
    pollInterval = POLL_INTERVAL_MIN;
    idleTicks = 0;
    evaluate();
  }

  function interceptCheckoutClicks(e) {
    var target = e.target.closest(CHECKOUT_SELECTOR);
    if (!target) return;
    if (target.classList.contains('thc-moq-disabled')) {
      e.preventDefault();
      e.stopPropagation();
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  document.addEventListener('click', interceptCheckoutClicks, true);
  document.addEventListener('DOMContentLoaded', evaluate);
  document.addEventListener('cart:updated', onCartEvent); // fires in many OS 2.0 themes
  document.addEventListener('cart:refresh', onCartEvent);

  evaluate();
})();
